import { FilterExpression } from "Engine/src/Helpers/FilterExpressions/FilterExpression";

import { ICandyApiDraftClient } from "../../../../../Common/CandyApiClient/ICandyApiClient";
import { AbsoluteResolvedModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { FilterExpressionToQueryCollectionExpressionVisitor } from "../../../Helpers/FilterExpressions/FilterExpressionToQueryCollectionExpressionVisitor";
import { AndExpression } from "../../../../../Common/CandyApiClient/Types/QueryCollecton/Expressions/AndExpression";
import { InnerQueryRequest } from "../../../../../Common/CandyApiClient/Types/QueryCollecton/InnerQueryRequest";
import { SelectClause } from "../../../../../Common/CandyApiClient/Types/QueryCollecton/SelectClause";
import { SumCallExpression } from "../../../../../Common/CandyApiClient/Types/QueryCollecton/Expressions/SumCallExpression";
import { ValueReferenceExpression } from "../../../../../Common/CandyApiClient/Types/QueryCollecton/Expressions/ValueReferenceExpression";
import { CountCallExpression } from "../../../../../Common/CandyApiClient/Types/QueryCollecton/Expressions/CountCallExpression";
import { reject } from "../../../../../Common/TypingUtils";
import { wrapDecimalOrUndefined } from "../../../Helpers/AutocalcCommonFunctions";

import {
    AggregationType,
    IAggregationQueryResult,
    IAggregationQueryStrategy,
    SelectAggregationClause,
} from "./IAggregationQueryStrategy";

export class ServerSideAggregationQueryStrategy implements IAggregationQueryStrategy {
    private readonly candyApiDraftClient: ICandyApiDraftClient;

    public constructor(candyApiDraftClient: ICandyApiDraftClient) {
        this.candyApiDraftClient = candyApiDraftClient;
    }

    public async query(
        multilinePath: AbsoluteResolvedModelPath,
        filters: FilterExpression[],
        selects: SelectAggregationClause[],
        abortSignal?: AbortSignal
    ): Promise<IAggregationQueryResult> {
        const requestSelects = selects.map(
            (x, index) => new SelectClause(x.name ?? "val" + index, this.getAggregationCallFunc(x))
        );
        const filterConverter = new FilterExpressionToQueryCollectionExpressionVisitor(multilinePath);
        const convertedFilters = filters.map(x => filterConverter.transformExpression(x));
        const combinedFilter =
            convertedFilters.length > 1 ? new AndExpression(convertedFilters) : convertedFilters?.[0];
        const request = new InnerQueryRequest(multilinePath.toLegacyPath(), requestSelects, combinedFilter);
        const response = await this.candyApiDraftClient.queryCollection(request, abortSignal);
        const firstDataRow = response.data[0];
        const aggResults =
            firstDataRow != undefined
                ? firstDataRow.map(value => wrapDecimalOrUndefined(value))
                : selects.map(_ => undefined);
        return {
            aggregations: aggResults,
        };
    }

    private getAggregationCallFunc(clause: SelectAggregationClause) {
        return clause.type === AggregationType.Sum
            ? new SumCallExpression(new ValueReferenceExpression(clause.relativeFieldPath.toString() + ".value"))
            : clause.type === AggregationType.Count
              ? new CountCallExpression()
              : reject(`Unsupported query expression [${clause}]`);
    }
}
